/**
 * Google Business Profile card on the Local SEO tab — header, "Listed / Not in pack"
 * status badge, the "Fix in 30 seconds" CID-paste instructions for the not-found state,
 * and the 4-stat grid (rating · position · phone · website) when GBP is found.
 */
import React from "react";
import { Crown, Phone, Globe, AlertCircle } from "lucide-react";
import { fmt } from "../../../lib/format";
import Stars from "./Stars";

function GbpStat({ label, value, hint, testid, icon: Icon }) {
  return (
    <div className="p-5" data-testid={testid}>
      <div className="text-[10px] uppercase tracking-widest text-[#a0aec0] font-semibold flex items-center gap-1.5">
        {Icon && <Icon className="h-3 w-3" />}
        {label}
      </div>
      <div className="mt-1.5">{value}</div>
      {hint && <div className="text-[10px] text-[#a0aec0] font-mono-tight mt-1 truncate" title={hint}>{hint}</div>}
    </div>
  );
}

export default function GbpCard({ client, gbp, loading }) {
  const g = gbp?.gbp;
  return (
    <div className="card-soft" data-testid="gbp-card">
      <div className="px-5 py-4 border-b border-[#edf2f7] flex items-center justify-between">
        <div className="flex items-start gap-3">
          <div className="h-9 w-9 rounded-xl bg-[#4fd1c5]/10 text-[#2c7a7b] inline-flex items-center justify-center shrink-0">
            <Crown className="h-4 w-4" />
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold">Your Google Business Profile</div>
            <h3 className="font-display text-base mt-0.5 text-[#1a202c]">{g?.title || client.name}</h3>
          </div>
        </div>
        {gbp?.found ? (
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#38b2ac] bg-[#4fd1c5]/10 px-2 py-1 rounded-md">
            Listed
          </span>
        ) : (
          <span className="text-[10px] uppercase tracking-widest font-bold text-[#dd6b20] bg-[#feebc8]/60 px-2 py-1 rounded-md inline-flex items-center gap-1">
            <AlertCircle className="h-3 w-3" />
            Not in Local Pack
          </span>
        )}
      </div>
      {loading ? (
        <div className="p-6 text-center text-xs text-[#a0aec0] font-mono-tight">Loading GBP data…</div>
      ) : !gbp?.found ? (
        <div className="p-6 text-center" data-testid="gbp-not-found">
          <p className="text-sm text-[#1a202c]">
            No Google Business Profile was found for <span className="font-semibold">{client.gbp_business_name || client.name}</span> in
            the latest Local Finder run for <span className="font-semibold">{client.service_location}</span>.
          </p>
          <p className="text-xs text-[#718096] mt-2 max-w-lg mx-auto">
            DataForSEO Local Finder uses the geo-centroid of the service location, so businesses on the edge
            of the search radius can be missed even when they appear in your personal Google results.
          </p>
          <div className="text-xs text-[#1a202c] mt-4 max-w-md mx-auto p-3 rounded-xl bg-[#f7fafc] border border-[#edf2f7] text-left">
            <div className="font-semibold mb-1.5">Fix in 30 seconds:</div>
            <ol className="list-decimal list-inside space-y-1 text-[#4a5568]">
              <li>Open <a href="https://www.google.com/maps" target="_blank" rel="noreferrer" className="text-[#2c7a7b] hover:underline">Google Maps</a> and find your business.</li>
              <li>Copy the long number after <code className="text-[10px] bg-white px-1 py-0.5 rounded border border-[#e2e8f0]">!1s0x…:0x</code> in the URL — that&apos;s your <span className="font-semibold">CID</span>.</li>
              <li>Click <span className="font-semibold">Edit</span> on this client and paste it into <span className="font-semibold">GBP CID</span>.</li>
            </ol>
            <div className="mt-2 text-[10px] text-[#a0aec0]">
              With a CID we look up your GBP directly from DataForSEO Business Listings — no geo-radius issues.
            </div>
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-0 divide-y md:divide-y-0 md:divide-x divide-[#edf2f7]">
          <GbpStat
            label="Rating"
            value={<Stars value={g.rating_value} />}
            hint={`${fmt(g.rating_votes)} review${g.rating_votes === 1 ? "" : "s"}`}
            testid="gbp-rating"
          />
          <GbpStat
            label="Best position"
            value={
              g.rank != null
                ? <span className="font-mono-tight text-xl text-[#1a202c]">#{g.rank}</span>
                : <span className="font-mono-tight text-xs text-[#a0aec0] uppercase tracking-widest">Direct lookup</span>
            }
            hint={g.rank != null ? "latest snapshot" : (g.source === "business_listings_direct" ? "via CID" : "—")}
            testid="gbp-position"
          />
          <GbpStat
            label="Phone"
            value={g.phone ? <span className="font-mono-tight text-sm text-[#1a202c]">{g.phone}</span> : <span className="text-[#a0aec0]">—</span>}
            hint={g.category || "Local business"}
            testid="gbp-phone"
            icon={Phone}
          />
          <GbpStat
            label="Website"
            value={g.domain ? (
              <a href={g.url || `https://${g.domain}`} target="_blank" rel="noreferrer" className="text-sm text-[#2c7a7b] hover:underline truncate inline-block max-w-full">
                {g.domain}
              </a>
            ) : <span className="text-[#a0aec0]">—</span>}
            hint={g.address || "—"}
            testid="gbp-website"
            icon={Globe}
          />
        </div>
      )}
    </div>
  );
}
